import * as signalR from '@microsoft/signalr';
import { apiUrl, wsCooldownMaxTime } from '../../../../config';
import { disconnectWSAPI } from '../../../helper/api/disconnectWS';
import retry from '../../../helper/api/retryRequest';
import ProviderEvents from './providerCommunicators/providerEvents';
import ProviderPosts from './providerCommunicators/providerPost';

const MILISECONDS_IN_SECONDS = 1000;
const SECONDS_IN_MINUTE = 60;

export default {
  props: {
    provider: { type: String, required: true },
    iRef: { default: '' }
  },
  data: vm => ({
    balance: Number(vm.$route.query.balance),
    popToken: vm.$route.query.popToken,
    connection: null,
    status: null,
    currency: '',
    cooldownEndDateTime: null,
    gamingEndInSeconds: null,
    gamingRemainingSeconds: null,
    isInitialBetPlayed: false,
    cdInterval: null,
    gameEndTimeout: null,
    gameEndInMs: null,
    gamingMaxTime: 0,
    cooldownMaxTime: Number(wsCooldownMaxTime),
    cooldownMaxTimeStatic: Number(wsCooldownMaxTime),
    isCDSet: false,
    isFeatureStarted: false,
    providerEventsManager: null,
    providerPostsManager: null,
    isLogoutBeaconSent: false
  }),
  async created () {
    if(this.$route.query.hideCooldown == 1) return; // Hidden cooldown if game type is alike to platinko

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/hub/notification?token=${this.popToken}`)
      .withAutomaticReconnect()
      .build();

    this.providerEventsManager = new ProviderEvents(this.provider, this.setFeatureState);
    this.providerPostsManager = new ProviderPosts(this.provider, this.$route);

    await this.start();
  },
  computed: {
    isCooldownActive: vm => vm.status === 'stopped' && vm.gamingRemainingSeconds < -1,
    displayedTime: vm => vm.isCooldownActive ? vm.cooldownEndDateTime : vm.gamingEndInSeconds
  },
  methods: {
    async start () {
      try {
        this.providerPostsManager.setWrapperPointerEvents('none');
        await retry(this.resolveConnection);
        await retry(this.syncAndAddListeners);
        this.providerPostsManager.setWrapperPointerEvents();
      } catch (err) {
        this.redirectOnMultipleErrors('START_ERROR');
      }
    },
    async resolveConnection () {
      try {
        await this.connection.start();
        this.connection._sendMessage(this.connection._cachedPingMessage);

        window.addEventListener('pagehide', () => this.disconnectWS('pagehide'));

        return Promise.resolve('Connected');
      }
      catch (err) {
        return Promise.reject(err);
      }
    },
    /** Sync timers and add listeners for WS connection (CD error and NotifyBalanceUpdate). */
    async syncAndAddListeners () {
      try { await this.callSyncTimer(); }
      catch (err) { return Promise.reject(err); }

      this.connection.on('CooldownError', this.callSyncTimer);
      this.connection.on('NotifyBalanceUpdate', ({ data }) => { this.balanceAndTimeUpdate(data) });
      return Promise.resolve('Synced');
    },
    /** Clear intervals and load data sent via WS.
     *
     * Possible states per POP documentation:
     *    1) Not started -> status === 'stopped' && gamingRemainingSeconds == -1 && no cooldownEndDateTime recieved
     *    2) In cooldown -> status === 'stopped' && cooldownTimeInSeconds >= 0 && cooldownEndDateTime recieved
     *    3) Running -> status === 'running' && gamingRemainingSeconds >= 0 && gamingEndDateTime recieved
     */
    async callSyncTimer () {
      this.clearIntervalAndNull();
      this.clearTimeoutAndNull();

      try {
        const {
          status, gamingRemainingSeconds, gamingEndDateTime, cooldownEndDateTime, gamingPeriodSeconds
        } = await this.connection.invoke('SyncTimer', `${this.popToken}`);

        if (cooldownEndDateTime && gamingEndDateTime && !this.isCDSet) {
          this.isCDSet = true;

          if (this.isInitialBetPlayed) {
            const cdTimeInMinutes = Math.ceil(Math.floor(((new Date(cooldownEndDateTime) - Date.now()) / MILISECONDS_IN_SECONDS)) / SECONDS_IN_MINUTE);
            this.cooldownMaxTimeStatic = cdTimeInMinutes * SECONDS_IN_MINUTE;
          }

          this.cooldownMaxTime = Math.floor(((new Date(cooldownEndDateTime) - Date.now()) / MILISECONDS_IN_SECONDS));
        }

        this.status = status;
        this.gamingMaxTime = Number(gamingPeriodSeconds);
        this.gameEndInMs = (gamingEndDateTime && new Date(gamingEndDateTime) - Date.now() > 0) ? new Date(gamingEndDateTime) - Date.now() : (gamingPeriodSeconds * 1000);
        this.gamingRemainingSeconds = Number(gamingRemainingSeconds);


        const cooldownTimeInSeconds = this.dateTimeUntilInSeconds(cooldownEndDateTime, 'ceil');
        const isNotStarted = status === 'stopped' && gamingRemainingSeconds == -1 && !cooldownEndDateTime;
        const isCooldown = status === 'stopped' && cooldownTimeInSeconds >= 0 && cooldownEndDateTime;
        const isRunning = status === 'running' && gamingRemainingSeconds >= 0 && gamingEndDateTime;

        if (isNotStarted) {
          console.log('Not started')
          this.setInitial();
          return Promise.resolve('NotStarted');
        }

        if (isCooldown) {
          console.log('In cool down')
          this.gamingEndInSeconds = 0;
          this.gamingRemainingSeconds = -2;
          this.cooldownEndDateTime = cooldownTimeInSeconds;
          this.isInitialBetPlayed = false;

          this.cdInterval = setInterval(this.intervalTimeUpdate, 1000);
          if (!this.isFeatureStarted) this.providerPostsManager.setProviderGameState(false);
          return Promise.resolve('CoolDown');
        }

        if (isRunning) {
          console.log('Running')
          this.gameEndTimeout = setTimeout(() => { this.setCoolDown() }, this.gameEndInMs);
          this.gamingEndInSeconds = this.gamingMaxTime - Number(gamingRemainingSeconds);
          this.cooldownEndDateTime = this.cooldownMaxTime - this.dateTimeUntilInSeconds(cooldownEndDateTime);

          this.cdInterval = setInterval(this.intervalTimeUpdate, 1000);
          this.providerPostsManager.setProviderGameState();
          return Promise.resolve('Running');
        }
      } catch (err) {
        return Promise.reject(err);
      }
    },
    intervalTimeUpdate () {
      if (!this.isCooldownActive) {
        this.gamingEndInSeconds += 1;
        this.cooldownEndDateTime += 1;

        if (this.cooldownEndDateTime >= this.cooldownMaxTime) return this.setInitial();

        const isLastSecond = this.gamingEndInSeconds == (this.gamingMaxTime - 1);
        const isCDEndAfterGamingEnd = (this.cooldownMaxTime - this.cooldownEndDateTime) > (this.gamingMaxTime - this.gamingEndInSeconds);
        const blockGameplay = isLastSecond && isCDEndAfterGamingEnd && !this.isFeatureStarted;

        if (blockGameplay) this.providerPostsManager.setProviderGameState(false);

        this.gamingEndInSeconds >= this.gamingMaxTime && this.setCoolDown();
      } else {
        this.cooldownEndDateTime -= 1;
        this.cooldownEndDateTime <= 0 && this.delayedSyncTimerCall();
      }
    },
    delayedSyncTimerCall () {
      this.clearIntervalAndNull();
      setTimeout(() => retry(this.callSyncTimer, 2000), 1500);
    },
    setInitial () {
      this.clearIntervalAndNull();
      this.clearTimeoutAndNull();
      this.providerPostsManager.setProviderGameState();

      this.gamingEndInSeconds = 0;
      this.cooldownEndDateTime = 0;
      this.gamingRemainingSeconds = -1;
      this.status = 'stopped';
    },
    setCoolDown () {
      this.clearIntervalAndNull();
      this.clearTimeoutAndNull();

      this.status = 'stopped';
      this.gamingRemainingSeconds = -2;
      this.cooldownEndDateTime = this.cooldownMaxTime - this.cooldownEndDateTime;
      this.cdInterval = setInterval(this.intervalTimeUpdate, 1000);
    },
    dateTimeUntilInSeconds (date, rounding = 'floor') {
      if (rounding === 'ceil') return Math.ceil((new Date(date) - Date.now()) / 1000);
      if (rounding === 'floor') return Math.floor((new Date(date) - Date.now()) / 1000);
    },
    disconnectWS (reason) {
      if (reason === 'pagehide' && !this.isLogoutBeaconSent) {
        this.isLogoutBeaconSent = true
        navigator.sendBeacon(`${apiUrl}/session/logout?reason=${reason}&token=${this.popToken}`);
      }
    },
    async balanceAndTimeUpdate (data) {
      let isBet = true;
      if (!this.currency) this.currency = data.lifeTimeStats.currencyCode;

      if (!this.isInitialBetPlayed) {
        this.isInitialBetPlayed = this.balance > data.balanceArray[0].balanceAmt;

        if (this.isInitialBetPlayed) {
          this.isCDSet = false;
          try { await retry(this.callSyncTimer, 2000); }
          catch (err) { this.redirectOnMultipleErrors('INITIAL_BET_SYNC_TIMER_ERROR'); }
        } else {
          this.balance = data.balanceArray[0].balanceAmt;
          return;
        }

      } else {
        isBet = this.balance < data.lifeTimeStats.lifetimeAmt;
        if (isBet) this.cooldownMaxTime = this.cooldownMaxTimeStatic;
      }

      if (isBet && this.isInitialBetPlayed) {
        this.cooldownEndDateTime = 0;
        if (this.status !== 'running') this.status = 'running';
        if (!this.cdInterval) this.cdInterval = setInterval(this.intervalTimeUpdate, 1000);
        if (!this.gameEndTimeout) this.gameEndTimeout = setTimeout(() => { this.setCoolDown() }, this.gameEndInMs);
      }

      this.balance = data.lifeTimeStats.lifetimeAmt;
    },
    clearIntervalAndNull () {
      if (this.cdInterval) {
        clearInterval(this.cdInterval);
        this.cdInterval = null;
      }
    },
    clearTimeoutAndNull () {
      if (this.gameEndTimeout) clearTimeout(this.gameEndTimeout);
      this.gameEndTimeout = null;
    },
    setFeatureState (isStarted = true) {
      this.isFeatureStarted = isStarted;
      if (this.isCooldownActive) {
        this.providerPostsManager.setProviderGameState(isStarted);
        this.providerPostsManager.setWrapperPointerEvents(isStarted ? 'auto' : 'none');
      }
    },
    async redirectOnMultipleErrors (reason) {
      await disconnectWSAPI(this.popToken, reason);

      this.isLogoutBeaconSent = true

      this.providerPostsManager.setWrapperPointerEvents();
      return this.$router.push({
        path: '/MessageView',
        query: { type: 'error', languageCode: 'CS', errorCode: 'WS error', text: '', closeurl: this.$route.query.closeurl }
      });
    }
  },
}
